// Which service categories can be booked in the logged-in app, all in one place.
//
// To pause or resume a service, edit this file and redeploy. Nothing else needs
// to change. Keys are the requestType values from GetServiceCategorySettings.
//
// Options per category:
//   showOnHome      - false hides the category's button on the /home screen
//                     (default true; the button also needs the category to exist
//                     in requestCategorySettings).
//   paused          - true: requests can still be submitted, but they cannot be
//                     confirmed. The cost card shows `pausedMessage` instead of
//                     Confirm Service.
//   pausedSubTypes  - pause only these sub-types, e.g. ['Plumbing'] for handyman
//                     or ['Babysitter'] for helper. The rest stay bookable. Handyman
//                     sub-types are the first booking question: "Electrical",
//                     "Plumbing", "Door, cupboard/cabinet".
//   allowAdvancePaymentWhenPaused - helper only: requests with an advance payment
//                     (the /booking/<id> flow) can still be confirmed while
//                     `paused` is true.
//   pausedMessage   - shown wherever a paused service would otherwise be booked.
//
// This is a build-time file. To switch services on and off without a deploy,
// move these values into the backend's category settings or app settings and
// read them here. The rest of the app only calls the helpers below, so nothing
// else would change.

const REVAMP_MESSAGE = (segment) =>
    `Our ${segment} services segment is undergoing a major revamp, which may take several weeks to be resumed. Apologies for any inconvenience caused.`

export const SERVICE_AVAILABILITY = {
    'Aircon Services': {
        showOnHome: true,
    },
    'Fitness Trainer': {
        showOnHome: true,
    },
    'Domestic Services': {
        showOnHome: true,
        // Standard helper bookings are paused; advance-payment bookings still work.
        paused: true,
        allowAdvancePaymentWhenPaused: true,
        // Blocked at the date/time step before a request is even created.
        pausedSubTypes: ['Elderly Care', 'Cooking Assistance', 'Babysitter'],
        pausedMessage: REVAMP_MESSAGE('helper'),
    },
    'Handyman Services': {
        showOnHome: true,
        // Only Electrical is open. Remove an entry to re-enable that sub-type.
        // Values are the key-1 service types from utils/handyman-quickbook.js.
        pausedSubTypes: ['Plumbing', 'Door, cupboard/cabinet', 'Drilling & Installation', 'Painting'],
        pausedMessage: REVAMP_MESSAGE('handyman'),
    },
}

const settingsFor = (requestType) => SERVICE_AVAILABILITY[requestType] || {}

export const isShownOnHome = (requestType) => settingsFor(requestType).showOnHome !== false

export const getPausedMessage = (requestType) =>
    settingsFor(requestType).pausedMessage || REVAMP_MESSAGE('service')

export const isSubTypePaused = (requestType, subType) =>
    (settingsFor(requestType).pausedSubTypes || []).includes(subType)

// A handyman request's sub-type is the answer to the first booking question,
// stored as { 1: "Electrical", criteria: "Handyman service type - Electrical" }.
// The quick-book flows (HandymanBooking, HandymanDealItems,
// /home/services/handyman) and the question wizard (ServiceCriteria) both store
// it under key "1". The criteria text is the fallback.
export const getHandymanSubType = (request) => {
    for (const c of request?.criteria || []) {
        if (typeof c?.['1'] === 'string') return c['1']
        const match = /Handyman service type - (.+)$/i.exec(c?.criteria || '')
        if (match) return match[1].trim()
    }
    return null
}

/**
 * Whether a submitted request may be confirmed and paid.
 * @param {object} request  the request, including criteria and serviceCosts
 */
export const canConfirmRequest = (request) => {
    const settings = settingsFor(request?.requestType)

    if (settings.paused) {
        const hasAdvancePayment = request?.serviceCosts?.advancePayment > 0
        if (!(settings.allowAdvancePaymentWhenPaused && hasAdvancePayment)) return false
    }

    if (request?.requestType === 'Handyman Services') {
        const subType = getHandymanSubType(request)
        // Unknown sub-type: allow it only when nothing is paused.
        if (!subType) return !(settings.pausedSubTypes || []).length
        return !isSubTypePaused('Handyman Services', subType)
    }

    return true
}
